import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select"; 
import { SEVERITY_LEVELS } from "../../enum";

const OptionFormModal = ({ show, onHide, option, questionId, onSuccess }) => {
    const [text, setText] = useState(option?.text || "");
    const [severityLevel, setSeverityLevel] = useState(option?.severity_level || 0);
    const [link, setLink] = useState(option?.link || "");
    const [specialities, setSpecialities] = useState([]);
    const [selectedSpecialities, setSelectedSpecialities] = useState([]);
    const [errors, setErrors] = useState({
        text: '',
        severityLevel: '',
        specialities: '',
        link: ''
    });

    const fetchSpecialities = async () => {
        try {
            const { data } = await axios.get("/speciality");
            setSpecialities(data.map((s) => ({ value: s.specialty_id, label: s.name })));
        } catch (error) {
            console.error("Error fetching specialities:", error);
            toast.error("Failed to fetch specialities.");
        }
    };

    useEffect(() => {
        fetchSpecialities();

        if (option) {
            setText(option.text || "");
            setSeverityLevel(option.severity_level || 0);
            setLink(option.link || "");
            setSelectedSpecialities(
                option.specialities
                    ? option.specialities.map((s) => ({ value: s.specialty_id, label: s.name }))
                    : []
            );
        } else {
            setText("");
            setSeverityLevel(0);
            setLink("");
            setSelectedSpecialities([]);
        }
    }, [option]);


    const isValid = useMemo(() => {
        let valid = true;

        if (!text.trim()) valid = false;
        if (severityLevel === 0) valid = false;
        if (selectedSpecialities.length === 0) valid = false;
        if (link && !/^https?:\/\/\S+/i.test(link)) valid = false;

        return valid;
    }, [text, severityLevel, selectedSpecialities, link]);

    
    const validate = () => {
        const newErrors = {
            text: '',
            severityLevel: '',
            specialities: '',
            link: ''
        };

        if (!text.trim()) newErrors.text = 'Text is required';
        if (severityLevel === 0) newErrors.severityLevel = 'Severity level is required';
        if (selectedSpecialities.length === 0) newErrors.specialities = 'At least one speciality is required';
        if (link && !/^https?:\/\/\S+/i.test(link)) newErrors.link = 'Invalid URL format';

        setErrors(newErrors);
        return isValid;
    };

    const handleSave = async () => {
        const isValidForm = validate();
        if (!isValidForm) {
            toast.error("Please correct the errors before saving.");
            return;
        }

        try {
            const specialityIds = selectedSpecialities.map((s) => s.value);

            if (option) {
                await axios.put(`/options/${option.option_id}`, {
                    text,
                    severityLevel,
                    link,
                    specialities: specialityIds,
                });
                toast.success("Option updated successfully.");
            } else {
                await axios.post(`/questions/${questionId}/options`, {
                    text,
                    severityLevel,
                    link,
                    specialities: specialityIds,
                });
                toast.success("Option added successfully.");
            }
            onSuccess();
            onHide();
        } catch (error) {
            console.error("Error saving option:", error);
            toast.error("Failed to save option.");
        }
    };

    return (
        <Modal show={show} style={{ marginTop: "60px" }} onHide={onHide} backdrop={false} centered>
            <Modal.Header closeButton>
                <Modal.Title>{option ? "Edit Option" : "Add Option"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group controlId="optionText">
                        <Form.Label>Option Text</Form.Label>
                        <Form.Control
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                        {errors.text && <Form.Text className="text-danger">{errors.text}</Form.Text>}
                    </Form.Group>

                    <Form.Group controlId="optionSeverityLevel">
                        <Form.Label>Severity Level</Form.Label>
                        <Form.Control
                            as="select"
                            value={severityLevel}
                            onChange={(e) => setSeverityLevel(Number(e.target.value))}
                        >
                            <option value="">Select Severity Level</option>
                            <option value={SEVERITY_LEVELS.MODERATE}>Moderate</option>
                            <option value={SEVERITY_LEVELS.SEVERE}>Severe</option>
                        </Form.Control>
                        {errors.severityLevel && (
                            <Form.Text className="text-danger">
                                {errors.severityLevel}
                            </Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group controlId="optionLink">
                        <Form.Label>Link</Form.Label>
                        <Form.Control
                            type="text"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                        />
                        {errors.link && (
                            <Form.Text className="text-danger">{errors.link}</Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group controlId="optionSpecialities">
                        <Form.Label>Specialities</Form.Label>
                        <Select
                            isMulti
                            value={selectedSpecialities}
                            onChange={setSelectedSpecialities}
                            options={specialities}
                            placeholder="Select specialities"
                        />
                        {errors.specialities && (
                            <Form.Text className="text-danger">
                                {errors.specialities}
                            </Form.Text>
                        )}
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSave} 
                    disabled={!isValid} 
                >
                    Save
                </Button>
            </Modal.Footer>
           
        </Modal>
    );
};

export default OptionFormModal;